import { Dialog, IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon'
import ChevronRightIcon from 'mdi-react/ChevronRightIcon'
import CloseIcon from 'mdi-react/CloseIcon'
import { FC, MouseEvent, TouchEvent, useCallback, useEffect, useRef, useState } from 'react'
import PhotoPlaceholder from './PhotoPlaceholder'

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: 28,
    // edge to edge on phones, the listing column has a 16px gutter
    [theme.breakpoints.down('xs')]: {
      margin: '0 -16px 20px'
    }
  },
  main: {
    position: 'relative',
    width: '100%',
    paddingTop: '62.5%',
    background: '#1f2421',
    borderRadius: 12,
    overflow: 'hidden',
    cursor: 'zoom-in',
    [theme.breakpoints.down('xs')]: {
      borderRadius: 0
    }
  },
  mainImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.9)',
    color: '#1f2421',
    padding: 6,
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    '&:hover': { background: '#fff' }
  },
  prev: { left: 12 },
  next: { right: 12 },
  counter: {
    position: 'absolute',
    right: 12,
    bottom: 12,
    padding: '4px 10px',
    borderRadius: 999,
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    fontSize: 13
  },
  thumbs: {
    position: 'relative',
    display: 'flex',
    gap: 8,
    marginTop: 8,
    overflowX: 'auto',
    paddingBottom: 4,
    scrollbarWidth: 'thin',
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 16,
      paddingRight: 16
    }
  },
  thumb: {
    flex: '0 0 auto',
    width: 144,
    height: 96,
    [theme.breakpoints.down('xs')]: {
      width: 112,
      height: 75
    },
    padding: 0,
    border: '2px solid transparent',
    borderRadius: 6,
    overflow: 'hidden',
    cursor: 'pointer',
    background: '#eee',
    opacity: 0.75,
    '& img': { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
    '&:hover': { opacity: 1 }
  },
  thumbActive: {
    borderColor: theme.palette.primary.main,
    opacity: 1
  },
  lightbox: {
    background: '#0d0f0e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    height: '100%'
  },
  lightboxImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain'
  },
  close: {
    position: 'absolute',
    top: 12,
    right: 12,
    color: '#fff',
    background: 'rgba(255,255,255,0.12)',
    '&:hover': { background: 'rgba(255,255,255,0.24)' }
  }
}))

interface GalleryProps {
  images: string[]
  alt: string
}

const SWIPE_THRESHOLD = 40

const Gallery: FC<GalleryProps> = ({ images, alt }) => {
  const classes = useStyles()
  const [index, setIndex] = useState(0)
  const [fullscreen, setFullscreen] = useState(false)
  const touchStartX = useRef<number | null>(null)
  const thumbsRef = useRef<HTMLDivElement>(null)
  const count = images.length

  const go = useCallback(
    (delta: number) => setIndex((current) => (current + delta + count) % count),
    [count]
  )

  useEffect(() => {
    if (!fullscreen) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') go(-1)
      if (event.key === 'ArrowRight') go(1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, fullscreen])

  // scroll only the thumbnail strip; scrollIntoView would also move the page
  useEffect(() => {
    const strip = thumbsRef.current
    const active = strip?.children[index] as HTMLElement | undefined
    if (!strip || !active) return
    strip.scrollTo({ left: active.offsetLeft - (strip.clientWidth - active.clientWidth) / 2, behavior: 'smooth' })
  }, [index])

  // warm the browser cache for the neighbours so paging feels instant
  useEffect(() => {
    if (count < 2) return
    ;[images[(index + 1) % count], images[(index - 1 + count) % count]].forEach((src) => {
      const img = new Image()
      img.src = src
    })
  }, [index, images, count])

  const onTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX
  }
  const onTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return
    const dx = event.changedTouches[0].clientX - touchStartX.current
    touchStartX.current = null
    if (Math.abs(dx) > SWIPE_THRESHOLD) go(dx < 0 ? 1 : -1)
  }

  if (!count) {
    return (
      <div className={classes.root}>
        <div className={classes.main} style={{ cursor: 'default' }}>
          <PhotoPlaceholder large />
        </div>
      </div>
    )
  }

  const arrows = count > 1 && (
    <>
      <IconButton
        aria-label='Previous photo'
        className={`${classes.arrow} ${classes.prev}`}
        onClick={(event: MouseEvent) => {
          event.stopPropagation()
          go(-1)
        }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        aria-label='Next photo'
        className={`${classes.arrow} ${classes.next}`}
        onClick={(event: MouseEvent) => {
          event.stopPropagation()
          go(1)
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </>
  )

  return (
    <div className={classes.root}>
      <div
        className={classes.main}
        role='button'
        tabIndex={0}
        aria-label='Open photos in full screen'
        onClick={() => setFullscreen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setFullscreen(true)
          }
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img className={classes.mainImage} src={images[index]} alt={`${alt} (${index + 1}/${count})`} />
        {arrows}
        <span className={classes.counter}>
          {index + 1} / {count}
        </span>
      </div>

      {count > 1 && (
        <div className={classes.thumbs} ref={thumbsRef}>
          {images.map((src, i) => (
            <button
              type='button'
              key={src}
              aria-label={`Show photo ${i + 1}`}
              className={`${classes.thumb} ${i === index ? classes.thumbActive : ''}`}
              onClick={() => setIndex(i)}
            >
              <img src={src} alt='' loading='lazy' />
            </button>
          ))}
        </div>
      )}

      <Dialog fullScreen open={fullscreen} onClose={() => setFullscreen(false)}>
        <div className={classes.lightbox} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <img className={classes.lightboxImage} src={images[index]} alt={`${alt} (${index + 1}/${count})`} />
          {arrows}
          <span className={classes.counter}>
            {index + 1} / {count}
          </span>
          <IconButton aria-label='Close' className={classes.close} onClick={() => setFullscreen(false)}>
            <CloseIcon />
          </IconButton>
        </div>
      </Dialog>
    </div>
  )
}

export default Gallery
